import React from "react";
import McpChatBalancePanel from "../balance/mcp-chat-balance-panel";
import { buildMcpChatBalancePanelProps } from "../balance/mcp-chat-balance-panel-props";
import McpChatInputArea from "../input/mcp-chat-input-area";
import { buildMcpChatInputAreaProps } from "../input/mcp-chat-input-area-props";
import McpChatSuggestions from "../input/mcp-chat-suggestions";
import McpChatMessageComponent from "../message/mcp-chat-message";
import McpChatThinkingIndicator from "../message/mcp-chat-thinking";
import McpChatSearchBar from "../search/mcp-chat-search-bar";
import McpChatSearchResults from "../search/mcp-chat-search-results";
import { isPlatformMode } from "../shared/mcp-chat-config";
import type { McpChatMessage, ParsedQuestionBlock, ParsedQuestionnaire } from "../shared/mcp-chat-types";
import McpChatEmptyState from "./mcp-chat-empty-state";
import McpChatErrorBoundary from "./mcp-chat-error-boundary";
import McpChatHeader from "./mcp-chat-header";
import { getMcpChatPanelInstance, requestUndock } from "./mcp-chat-panel-ref";
import McpChatTabBar from "./mcp-chat-tab-bar";
import "./mcp-chat-panel.scss";

/**
 * McpChatDocked renders the MCP chat content inside a panel (left or right)
 * without floating chrome (no wrapper positioning, no FAB, no resize handle, no shadow).
 *
 * It re-uses the singleton McpChatPanel instance that holds all chat state.
 */
interface McpChatDockedState {
  tick: number;
}

class McpChatDocked extends React.PureComponent<{}, McpChatDockedState> {
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(props: {}) {
    super(props);
    this.state = { tick: 0 };
  }

  componentDidMount() {
    // Poll to keep in sync with the shared panel's state changes.
    this.intervalId = setInterval(() => {
      this.setState((prev) => ({ tick: prev.tick + 1 }));
    }, 200);
  }

  componentWillUnmount() {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  handleUndock = () => {
    requestUndock("bottom-right");
  };

  /** When the user drags the header, undock and start floating drag immediately. */
  handleDragStart = (e: React.MouseEvent) => {
    requestUndock("bottom-right", { clientX: e.clientX, clientY: e.clientY });
  };

  render() {
    const panel = getMcpChatPanelInstance();
    if (!panel) {
      return (
        <div className="mcp-chat-docked">
          <div className="mcp-chat-docked__empty">Loading AI Chat...</div>
        </div>
      );
    }

    const { state } = panel;
    const activeTab = panel.getActiveTab();
    if (!activeTab) return null;

    const { messages, isLoading, inputValue } = activeTab;
    const hasMessages = messages.length > 0;
    const visibleData = panel.getVisibleMessages();
    const visibleMessages = visibleData.visibleMessages;
    const hiddenCount = visibleData.hiddenCount;
    const mostRecentUndoableId = panel.getMostRecentUndoableMessageId();
    let latestAssistantIdForUndo: string | null = null;
    if (mostRecentUndoableId) {
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.role === "assistant" && !m.isError) {
          latestAssistantIdForUndo = m.id;
          break;
        }
      }
    }
    // Latest non-error assistant — pins its OK/Not OK/Copy footer visible.
    let lastAssistantId: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant" && !m.isError) {
        lastAssistantId = m.id;
        break;
      }
    }
    const balancePanelProps = buildMcpChatBalancePanelProps(panel);
    const { search } = state;

    return (
      <div className="mcp-chat-docked">
        <McpChatHeader
          isWsConnected={state.isWsConnected}
          hasMessages={hasMessages}
          isLoading={isLoading}
          chatMode={activeTab.chatMode}
          planMode={activeTab.planMode}
          onClearChat={() => panel.handleClearChat()}
          onClose={this.handleUndock}
          onDragStart={this.handleDragStart}
          isDocked
          onSearchToggle={() => panel.handleSearchToggle()}
          isSearchOpen={search.isOpen}
          onHistoryOpen={state.rewindEnabled === true ? () => panel.handleHistoryOpen() : undefined}
          isHistoryOpen={!!state.historyModal}
        />

        <McpChatTabBar
          ref={panel.tabBarRef}
          tabs={state.tabs}
          activeTabId={state.activeTabId}
          onSwitchTab={(id: string) => panel.handleSwitchTab(id)}
          onCloseTab={(id: string) => panel.handleCloseTab(id)}
          onAddTab={() => panel.handleAddTab()}
          onRenameTab={(id: string, name: string) => panel.handleRenameTab(id, name)}
        />

        {search.isOpen && (
          <McpChatSearchBar
            searchState={search}
            onQueryChange={(q: string) => panel.handleSearchQueryChange(q)}
            onNextMatch={() => panel.handleSearchNextMatch()}
            onPrevMatch={() => panel.handleSearchPrevMatch()}
            onToggleCrossTab={() => panel.handleSearchToggleCrossTab()}
            onClose={() => panel.handleSearchToggle()}
          />
        )}

        {search.isOpen && search.crossTab && search.matches.length > 0 && (
          <McpChatSearchResults
            matches={search.matches}
            tabs={state.tabs}
            currentMatchIndex={search.currentMatchIndex}
            onSelectMatch={(idx: number) => panel.handleSearchSelectMatch(idx)}
          />
        )}

        <div
          className={!hasMessages && !isLoading ? "mcp-chat__messages mcp-chat__messages--empty" : "mcp-chat__messages"}
          ref={panel.messagesContainerRef}
          role="log"
          aria-live="polite"
          onScroll={panel.handleMessagesScroll}
        >
          {!hasMessages && !isLoading && (
            <McpChatEmptyState
              capabilities={state.capabilities}
              expandedCategory={activeTab.expandedCategory}
              onCategoryClick={(catId: string) => panel.handleCategoryClick(catId)}
              onSuggestionClick={(text: string) => panel.handleSuggestionClick(text)}
              isPlatformMode={isPlatformMode()}
              chatMode={activeTab.chatMode}
              planMode={activeTab.planMode}
            />
          )}
          {hiddenCount > 0 && (
            <button className="mcp-chat__load-more" onClick={() => panel.handleLoadMore()}>
              Load {hiddenCount} more
            </button>
          )}
          {visibleMessages.map((msg: McpChatMessage) => (
            <McpChatErrorBoundary key={msg.id}>
              <div data-message-id={msg.id}>
                <McpChatMessageComponent
                  message={msg}
                  isActiveTabLoading={isLoading}
                  isLastAssistant={lastAssistantId === msg.id}
                  onRate={(chatRequestId: string, rating: number, reason?: string) =>
                    panel.handleRateResponse(chatRequestId, rating, reason)
                  }
                  isMostRecentUndoable={mostRecentUndoableId === msg.id}
                  rewindEnabled={state.rewindEnabled === true}
                  undoButtonTargetUserMsgId={latestAssistantIdForUndo === msg.id ? mostRecentUndoableId : null}
                  questionnaireProgress={activeTab.questionnaires[msg.id]}
                  tabPlanMode={activeTab.planMode}
                  planJson={activeTab.planJson}
                  searchQuery={search.isOpen ? search.query : undefined}
                  isActiveSearchMatch={
                    search.isOpen &&
                    search.currentMatchIndex >= 0 &&
                    search.matches[search.currentMatchIndex]?.messageId === msg.id
                  }
                  onRetry={(id: string) => panel.handleRetry(id)}
                  onUndo={(id: string) => panel.handleRewindRequest(id)}
                  onPlanApprove={(id: string) => panel.handlePlanApprove(id)}
                  onPlanReject={(id: string) => panel.handlePlanReject(id)}
                  onSpecApprove={(id: string) => panel.handleSpecApprove(id)}
                  onSpecReject={(id: string) => panel.handleSpecReject(id)}
                  onChangeApprove={(id: string, deltaId: string) => panel.handleChangeApprove(id, deltaId)}
                  onChangeReject={(id: string, deltaId: string) => panel.handleChangeReject(id, deltaId)}
                  onInterruptApprove={(id: string) => panel.handleInterruptApprove(id)}
                  onInterruptApproveAll={(id: string) => panel.handleInterruptApproveAll(id)}
                  onInterruptReject={(id: string) => panel.handleInterruptReject(id)}
                  onOptionSelect={(text: string) => panel.handleOptionSelect(text)}
                  onQuestionOptionSelect={(
                    messageId: string,
                    qi: number,
                    val: string,
                    qc: number,
                    question?: ParsedQuestionBlock
                  ) => panel.handleQuestionOptionSelect(activeTab.id, messageId, qi, val, qc, question)}
                  onQuestionMultiToggle={(messageId: string, qi: number, val: string) =>
                    panel.handleQuestionMultiToggle(activeTab.id, messageId, qi, val)
                  }
                  onQuestionCustomValueChange={(messageId: string, qi: number, cv: string) =>
                    panel.handleQuestionCustomValueChange(activeTab.id, messageId, qi, cv)
                  }
                  onQuestionGoTo={(messageId: string, targetIndex: number) =>
                    panel.handleQuestionGoTo(activeTab.id, messageId, targetIndex)
                  }
                  onQuestionBack={(messageId: string) => panel.handleQuestionBack(activeTab.id, messageId)}
                  onQuestionNext={(messageId: string, qc: number, currentQuestion?: ParsedQuestionBlock) =>
                    panel.handleQuestionNext(activeTab.id, messageId, qc, currentQuestion)
                  }
                  onQuestionSubmit={(messageId: string, q: ParsedQuestionnaire) =>
                    panel.handleQuestionSubmit(activeTab.id, messageId, q)
                  }
                  onQuestionInterruptSubmit={(messageId: string, q: ParsedQuestionnaire) =>
                    panel.handleQuestionInterruptSubmit(messageId, q)
                  }
                  onQuestionDelegateOne={(messageId: string, qi: number, qc: number) =>
                    panel.handleQuestionDelegateOne(activeTab.id, messageId, qi, qc)
                  }
                  onQuestionDelegateRest={(messageId: string, q: ParsedQuestionnaire) =>
                    panel.handleQuestionDelegateRest(messageId, q)
                  }
                />
              </div>
            </McpChatErrorBoundary>
          ))}
          {isLoading && (
            <McpChatThinkingIndicator
              toolActivities={activeTab.toolActivities}
              // Docked mode omitted startTime, so useElapsedTimer short-circuited
              // and a stalled turn rendered a completely static screen — no clock,
              // no motion at all. Same source as the floating panel.
              startTime={panel.requestStartTimes.get(activeTab.id)}
              phaseTitle={activeTab.phaseTitle}
              toolTitle={activeTab.toolTitle}
            />
          )}
          <div ref={panel.messagesEndRef} />
        </div>

        {activeTab.queuedMessages.length > 0 && (
          <div className="mcp-chat__queued-banner">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="mcp-chat__queued-text">
              {activeTab.queuedMessages.length === 1
                ? "1 message queued"
                : `${activeTab.queuedMessages.length} messages queued`}
            </span>
            <button
              className="mcp-chat__queued-cancel"
              onClick={() => panel.handleCancelAllQueuedMessages()}
              aria-label="Cancel all queued messages"
            >
              Cancel all
            </button>
          </div>
        )}

        <McpChatSuggestions
          messages={activeTab.messages}
          isLoading={isLoading}
          inputValue={inputValue}
          onSuggestionClick={(text: string) => panel.handleSuggestionClick(text)}
        />

        <div className="mcp-chat__input-wrapper">
          {balancePanelProps ? <McpChatBalancePanel {...balancePanelProps} /> : null}
          <McpChatInputArea ref={panel.inputAreaRef} {...buildMcpChatInputAreaProps(panel, activeTab)} />
        </div>
      </div>
    );
  }
}

export default McpChatDocked;
